import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { CourseDetailsService } from '@/domain/services/course-details.service'
import { CourseRepository } from '@/infrastructure/repositories/course.repository'
import { createApiClient } from '@/infrastructure/api/client'
import type { CourseDetailsDisplay, MapCourseDetailsOptions } from '@/domain/entities/course-details.entity'
import { ErrorHandler } from '@/infrastructure/errors'
import { useErrors } from '../composables/app/useErrors'

/**
 * Store для страницы деталей курса (только чтение).
 * Отдельный loading/error, сброс при уходе со страницы не трогает список курсов.
 */
export const useCourseDetailsStore = defineStore('course-details', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('course-details')
  const display = ref<CourseDetailsDisplay | null>(null)
  const loading = ref(false)

  const hasCourseDetails = computed(() => display.value?.course != null)

  const apiClient = createApiClient({
    baseUrl: useRuntimeConfig().public.NUXT_APP_LANDING_API,
  })
  const courseRepository = new CourseRepository(apiClient)
  const courseDetailsService = new CourseDetailsService(courseRepository)

  async function fetchCourseDetailsBySlug(
    slug: string,
    options?: MapCourseDetailsOptions,
  ): Promise<CourseDetailsDisplay> {
    display.value = null
    clearErrors()
    loading.value = true
    try {
      const result = await courseDetailsService.getCourseDetailsBySlug(slug, options)
      display.value = result
      return result
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      throw handledError
    } finally {
      loading.value = false
    }
  }

  function reset() {
    display.value = null
    loading.value = false
    clearErrors()
  }

  return {
    display,
    loading,
    error,
    hasCourseDetails,
    hasError,
    fetchCourseDetailsBySlug,
    reset,
  }
})
